import { Suspense, lazy } from "react";
import { Navigate, type RouteObject } from "react-router";

import type {
  DashboardProjectsDetailsQuery,
  DashboardProjectsDocumentsDetailsQuery,
} from "@app_schema";

import type { Breadcrumb } from "@application/types/breadcrumb";

import {
  DASHBOARD_PROJECTS_DETAILS_CACHED_LOADER,
  DASHBOARD_PROJECTS_DETAILS_LOADER,
} from "@application/loaders/dashboard_projects_detail_loader";
import { DASHBOARD_PROJECTS_DOCUMENTS_DETAILS_LOADER } from "@application/loaders/dashboard_projects_documents_details_loader";
import { DASHBOARD_PROJECTS_LIST_LOADER } from "@application/loaders/dashboard_projects_list_loader";
import {
  DASHBOARD_RUN_REPORT_REVIEW_LOADER,
  type DashboardRunReportReviewLoaderData,
} from "@application/loaders/dashboard_run_report_review_loader";

import { DASHBOARD_PROJECTS_LINK } from "@application/navigation/dashboard_projects_link";

import { Loader } from "@styled/loader";

import { DashboardProjectsDetails } from "./projects_details";
import { DashboardProjectsDocumentsDetails } from "./projects_documents_details";
import { DashboardProjectsDocumentsList } from "./projects_documents_list";
import { DashboardProjectsEdit } from "./projects_edit";
import { DashboardProjectsList } from "./projects_list";

const DashboardRunReportReview = lazy(() =>
  import("@application/components/dashboard/runs/report_review").then((m) => ({
    default: m.DashboardRunReportReview,
  })),
);

export const DASHBOARD_PROJECTS_ROUTE = {
  path: "projects",
  handle: DASHBOARD_PROJECTS_LINK,
  children: [
    {
      index: true,
      element: <DashboardProjectsList />,
      loader: DASHBOARD_PROJECTS_LIST_LOADER,
    },
    {
      path: "new",
      element: <Navigate to="/dashboard/projects" replace />,
    },
    {
      path: ":id",
      loader: DASHBOARD_PROJECTS_DETAILS_LOADER,
      handle: (data: DashboardProjectsDetailsQuery): Breadcrumb => {
        return {
          name: data.project.name,
          to: `/dashboard/projects/${data.project.id}`,
        };
      },
      children: [
        {
          index: true,
          element: <DashboardProjectsDetails />,
          // No loader: the `:id` parent above runs the same one, and both
          // matching meant two project queries on every open.
        },
        {
          path: "edit",
          element: <DashboardProjectsEdit />,
          loader: DASHBOARD_PROJECTS_DETAILS_CACHED_LOADER,
          handle: (data: DashboardProjectsDetailsQuery): Breadcrumb => ({
            name: "Edit",
            to: `/dashboard/projects/${data.project.id}/edit`,
          }),
        },
        {
          path: "documents",
          loader: DASHBOARD_PROJECTS_DETAILS_CACHED_LOADER,
          handle: (data: DashboardProjectsDetailsQuery): Breadcrumb => ({
            name: "Documents",
            to: `/dashboard/projects/${data.project.id}/documents`,
          }),
          children: [
            {
              index: true,
              element: <DashboardProjectsDocumentsList />,
            },
            {
              path: ":documentID",
              element: <DashboardProjectsDocumentsDetails />,
              loader: DASHBOARD_PROJECTS_DOCUMENTS_DETAILS_LOADER,
              handle: (
                data: DashboardProjectsDocumentsDetailsQuery,
              ): Breadcrumb => ({
                name: data.project.document.name,
                to: `/dashboard/projects/${data.project.id}/documents/${data.project.document.id}`,
              }),
            },
          ],
        },
        {
          path: "records-review/:runId",
          element: (
            <Suspense fallback={<Loader />}>
              <DashboardRunReportReview />
            </Suspense>
          ),
          loader: DASHBOARD_RUN_REPORT_REVIEW_LOADER,
          handle: (data: DashboardRunReportReviewLoaderData): Breadcrumb => ({
            name: "Review Records",
            to: `/dashboard/projects/${data.projectID}/records-review/${data.runId}`,
          }),
        },
      ],
    },
  ],
} satisfies RouteObject;
